import { HTMLAttributes, Key, ReactNode, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import NaturePeopleOutlinedIcon from "@mui/icons-material/NaturePeopleOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { CustomAutocomplete } from "../components/Inputs/CustomAutocomplete";
import { useDebounce } from "../hooks/debounce";
import {
  clearPropertyLocation,
  fetchStaticMap,
  getPropertyLocation,
  savePropertyLocation,
  searchLocations,
} from "./api";
import { LocationCandidate, PropertyLocation } from "./types";

const MIN_QUERY_LENGTH = 3;
// Mirrors the backend cap on the search query; keeps a pasted page of text
// from reaching the geocoder.
const MAX_QUERY_LENGTH = 256;
const SEARCH_DEBOUNCE_MS = 350;

interface PropertyLocationFieldProps {
  propertyId: number;
  // Seeds the search so the property's own name is one focus away from results.
  propertyName?: string;
  // ISO-2 codes to bias/restrict the search to (e.g. ["fr", "es"]).
  countryCodes?: string[];
}

// A normalized view of the current location, from either a saved row or the
// candidate being saved (so the card can render optimistically before refetch).
interface LocationView {
  address: string | null;
  city: string | null;
  subregion: string | null;
  region: string | null;
  countryCode: string | null;
  latitude: number | null;
  longitude: number | null;
}

const viewFromCandidate = (c: LocationCandidate): LocationView => ({
  address: c.address_display,
  city: c.city,
  subregion: c.subregion,
  region: c.region,
  countryCode: c.country_code,
  latitude: c.latitude,
  longitude: c.longitude,
});

const viewFromSaved = (l: PropertyLocation): LocationView => ({
  address: l.address_display,
  city: l.city,
  subregion: l.subregion,
  region: l.region,
  countryCode: l.country_code,
  latitude: l.latitude,
  longitude: l.longitude,
});

const isCamping = (category: string | null): boolean =>
  !!category?.startsWith("camping");

const isAccommodation = (category: string | null): boolean =>
  !!category?.startsWith("accommodation");

const typeIcon = (category: string | null): ReactNode => {
  if (isCamping(category)) {
    return <NaturePeopleOutlinedIcon fontSize="small" />;
  }
  if (isAccommodation(category)) {
    return <HotelOutlinedIcon fontSize="small" />;
  }
  // A plain address (no property category) gets a neutral location pin.
  return <PlaceOutlinedIcon fontSize="small" />;
};

// ISO-3166-1 alpha-2 → regional-indicator flag emoji ("fr" → 🇫🇷).
const countryFlag = (code: string | null): string =>
  code && code.length === 2
    ? code
        .toUpperCase()
        .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)))
    : "";

const joinParts = (parts: Array<string | null | undefined>): string =>
  parts.filter((part): part is string => Boolean(part)).join(" · ");

// City + flag first: it is what disambiguates same-named hotels across towns.
const placeLine = (
  countryCode: string | null,
  city: string | null,
  rest: Array<string | null | undefined>
): string => {
  const flag = countryFlag(countryCode);
  const head = [flag, city].filter(Boolean).join(" ") || null;
  return joinParts([head, ...rest]);
};

const osmUrl = (lat: number, lon: number): string =>
  `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;

const MAP_HEIGHT = 480;

// A pin-on-map thumbnail — the visceral "yes, that's the right one" confirmation.
// Served through our backend proxy, fetched as an authenticated blob.
const PropertyLocationMap = ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}) => {
  const { data: blob } = useQuery(
    ["property-location-map", latitude, longitude],
    () => fetchStaticMap(latitude, longitude),
    { staleTime: Infinity }
  );
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return (
    <Box
      sx={{
        width: "100%",
        height: MAP_HEIGHT,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "action.hover",
        backgroundImage: src ? `url(${src})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
  );
};

export const PropertyLocationField = ({
  propertyId,
  propertyName = "",
  countryCodes = [],
}: PropertyLocationFieldProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState(propertyName);
  // Don't hit the geocoder on dialog open — only once the user engages.
  const [touched, setTouched] = useState(false);

  const debouncedQuery = useDebounce(inputValue, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedQuery.trim();
  const hasQuery = trimmedQuery.length >= MIN_QUERY_LENGTH;

  const locationKey = ["property-location", propertyId];
  const invalidateLocation = () => queryClient.invalidateQueries(locationKey);

  const locationQuery = useQuery(locationKey, () =>
    getPropertyLocation(propertyId)
  );

  const searchQuery = useQuery(
    ["geocoding-search", trimmedQuery, countryCodes],
    () => searchLocations(trimmedQuery, countryCodes),
    { enabled: touched && hasQuery, keepPreviousData: true }
  );

  const saveMutation = useMutation(
    (candidate: LocationCandidate) =>
      savePropertyLocation(propertyId, candidate.feature),
    {
      onSuccess: () => {
        setInputValue("");
        invalidateLocation();
      },
    }
  );

  const clearMutation = useMutation(() => clearPropertyLocation(propertyId), {
    onSuccess: () => {
      saveMutation.reset();
      invalidateLocation();
    },
  });

  // Current location, preferring the in-flight/just-saved pick over the fetched
  // row so a click confirms instantly (no "did it work?" gap).
  let view: LocationView | null = null;
  if (saveMutation.isLoading && saveMutation.variables) {
    view = viewFromCandidate(saveMutation.variables);
  } else if (saveMutation.data) {
    view = viewFromSaved(saveMutation.data);
  } else if (locationQuery.data) {
    view = viewFromSaved(locationQuery.data);
  }

  const options = searchQuery.data ?? [];
  const isSearching = touched && hasQuery && searchQuery.isFetching;
  const isBusy = saveMutation.isLoading || clearMutation.isLoading;
  const hasError = saveMutation.isError || clearMutation.isError;

  return (
    <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
      <Typography variant="subtitle2">
        <FormattedMessage id="PROPERTY_LOCATION_TITLE" />
      </Typography>

      {view && (
        <Stack
          spacing={1}
          sx={{
            p: 1.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "flex-start", minWidth: 0 }}
          >
            <PlaceOutlinedIcon
              color="primary"
              fontSize="small"
              sx={{ mt: 0.25 }}
            />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {view.address}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                noWrap
              >
                {placeLine(view.countryCode, view.city, [
                  view.subregion,
                  view.region,
                ])}
              </Typography>
              {view.latitude != null && view.longitude != null && (
                <Link
                  href={osmUrl(view.latitude, view.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                >
                  <FormattedMessage id="PROPERTY_LOCATION_VIEW_ON_MAP" /> ↗
                </Link>
              )}
            </Box>
            {saveMutation.isLoading ? (
              <CircularProgress size={16} sx={{ mt: 0.5 }} />
            ) : (
              <Tooltip
                title={intl.formatMessage({ id: "PROPERTY_LOCATION_REMOVE" })}
              >
                <span>
                  <IconButton
                    aria-label={intl.formatMessage({
                      id: "PROPERTY_LOCATION_REMOVE",
                    })}
                    size="small"
                    disabled={isBusy}
                    onClick={() => clearMutation.mutate()}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
          {view.latitude != null && view.longitude != null && (
            <PropertyLocationMap
              latitude={view.latitude}
              longitude={view.longitude}
            />
          )}
        </Stack>
      )}

      <CustomAutocomplete<LocationCandidate>
        formStyle={false}
        options={options}
        // The server already ranks and filters; never re-filter client-side.
        filterOptions={(opts) => opts}
        getOptionLabel={(option) => option.address_display ?? ""}
        isOptionEqualToValue={(option, value) =>
          option.provider_place_id === value.provider_place_id
        }
        value={null}
        inputValue={inputValue}
        onInputChange={(_, value) => setInputValue(value)}
        onChange={(_, candidate) => {
          if (candidate) {
            saveMutation.mutate(candidate);
          }
        }}
        loading={isSearching}
        disabled={isBusy}
        // It is a search field, not a select — no dropdown chevron.
        popupIcon={null}
        loadingText={intl.formatMessage({ id: "PROPERTY_LOCATION_SEARCHING" })}
        noOptionsText={intl.formatMessage({
          id: hasQuery
            ? "PROPERTY_LOCATION_NO_RESULTS"
            : "PROPERTY_LOCATION_HINT",
        })}
        renderOption={(props, option) => {
          const { key, ...liProps } = props as HTMLAttributes<HTMLLIElement> & {
            key?: Key;
          };
          return (
            <Box
              component="li"
              key={option.provider_place_id ?? key}
              {...liProps}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: "flex-start", width: "100%", py: 0.25 }}
              >
                <Box sx={{ mt: 0.25, color: "text.secondary" }}>
                  {typeIcon(option.category)}
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" noWrap>
                    {option.address_display}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {placeLine(option.country_code, option.city, [
                      option.postcode,
                    ])}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        }}
        onFocus={() => setTouched(true)}
        error={hasError}
        label={intl.formatMessage({
          id: view
            ? "PROPERTY_LOCATION_CHANGE_LABEL"
            : "PROPERTY_LOCATION_SEARCH_LABEL",
        })}
        placeholder={intl.formatMessage({
          id: "PROPERTY_LOCATION_SEARCH_PLACEHOLDER",
        })}
        helperText={
          hasError ? (
            <FormattedMessage id="PROPERTY_LOCATION_SAVE_ERROR" />
          ) : hasQuery ? undefined : (
            <FormattedMessage
              id="PROPERTY_LOCATION_MIN_CHARS"
              values={{ count: MIN_QUERY_LENGTH }}
            />
          )
        }
        textFieldSlotProps={{ htmlInput: { maxLength: MAX_QUERY_LENGTH } }}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" color="action" />
          </InputAdornment>
        }
      />
    </Stack>
  );
};
